import { NavLink } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  AlertTriangle,
  Bell,
  CircleGauge,
  Coins,
  FileText,
  LayoutGrid,
  LineChart,
  Newspaper,
  Server,
  ShieldCheck,
  Smartphone,
  Users,
} from 'lucide-react';

import { LogoFull } from './logo';

interface NavItem {
  readonly to: string;
  readonly label: string;
  readonly icon: LucideIcon;
}

interface NavGroup {
  readonly label: string;
  readonly items: readonly NavItem[];
}

/**
 * Owner-dashboard navigation. Grouped per the plan:
 *   PULSE       — at-a-glance overview
 *   PRODUCT     — users, content, channels, alerts, push (the things the app
 *                  serves)
 *   ENGAGEMENT  — mobile platforms, analytics funnels (how it's used)
 *   OPERATIONS  — errors, infra, costs (running it)
 *   GOVERNANCE  — audit (record of what was done)
 *
 * Sub-pages live as tabs at the top of each section page (not nested
 * sidebar entries), keeping the sidebar scannable.
 */
const groups: readonly NavGroup[] = [
  {
    label: 'Pulse',
    items: [{ to: '/', label: 'Overview', icon: LayoutGrid }],
  },
  {
    label: 'Product',
    items: [
      { to: '/users', label: 'Users', icon: Users },
      { to: '/content', label: 'Content', icon: FileText },
      { to: '/channels', label: 'Channels', icon: Newspaper },
      { to: '/alerts', label: 'Alerts', icon: AlertTriangle },
      { to: '/notifications', label: 'Notifications', icon: Bell },
    ],
  },
  {
    label: 'Engagement',
    items: [
      { to: '/mobile', label: 'Mobile', icon: Smartphone },
      { to: '/analytics', label: 'Analytics', icon: LineChart },
    ],
  },
  {
    label: 'Operations',
    items: [
      { to: '/errors', label: 'Errors', icon: Activity },
      { to: '/infra', label: 'Infra', icon: Server },
      { to: '/costs', label: 'Costs', icon: Coins },
    ],
  },
  {
    label: 'Governance',
    items: [{ to: '/audit', label: 'Audit', icon: ShieldCheck }],
  },
];

export function Sidebar({
  open = false,
  onNavigate,
}: {
  readonly open?: boolean;
  readonly onNavigate?: () => void;
}) {
  return (
    <aside className={open ? 'nw-sidebar nw-sidebar--open' : 'nw-sidebar'}>
      <div className="nw-sidebar__brand">
        <LogoFull size={28} tagline="Admin" />
      </div>
      {groups.map((g) => (
        <div key={g.label} className="nw-sidebar__group">
          <div className="nw-sidebar__group-label">
            <CircleGauge size={0} style={{ display: 'none' }} />
            {g.label}
          </div>
          {g.items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              onClick={onNavigate}
              className={({ isActive }) =>
                isActive
                  ? 'nw-sidebar__item nw-sidebar__item--active'
                  : 'nw-sidebar__item'
              }
            >
              <span className="nw-sidebar__item-icon">
                <item.icon size={20} strokeWidth={2} />
              </span>
              <span>{item.label}</span>
            </NavLink>
          ))}
        </div>
      ))}
    </aside>
  );
}
